import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { Text, type TextProps, TextTooltip } from '@components/atoms';
import { cn } from '@utils';

const ShortDetailLayoutVariants = cva('flex min-w-0 items-center gap-1', {
	variants: {
		color: {
			primary: 'text-primary',
			secondary: 'text-secondary',
			accent: 'text-accent',
		},
	},
	defaultVariants: {
		color: 'primary',
	},
});

interface ShortDetailLayoutProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
		Omit<TextProps, 'children' | 'color'>,
		VariantProps<typeof ShortDetailLayoutVariants> {
	icon: React.ReactElement;
	contentClassName?: string;
	truncate?: boolean;
}

const ShortDetailLayout = React.forwardRef<HTMLDivElement, ShortDetailLayoutProps>(
	({ className, icon, color = 'primary', weight = 'bold', children, truncate = true, contentClassName, ...props }, ref) => {
		const contentClasses = cn(
			typeof children !== 'string' || !truncate ? 'whitespace-nowrap' : '',
			props.onClick && 'cursor-pointer',
			contentClassName
		);

		const contentComponent: React.ReactNode =
			typeof children === 'string' && truncate ? (
				<TextTooltip color={color} weight={weight} className={contentClasses}>
					{children}
				</TextTooltip>
			) : (
				<Text color={color} weight={weight} className={contentClasses}>
					{children}
				</Text>
			);

		return (
			<div
				ref={ref}
				className={cn(ShortDetailLayoutVariants({ color, className }), truncate && 'min-w-0')}
				data-aid="short-detail-layout"
				{...props}
			>
				<div>{icon}</div>
				{contentComponent}
			</div>
		);
	}
);
ShortDetailLayout.displayName = 'ShortDetailLayout';

export { ShortDetailLayout };
